// react
import { useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';

// material ui
import {
  Alert,
  AlertTitle,
  Box,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';

// shared components
import InfoTooltipIcon from '@/components/ui/icon/InfoTooltipIcon';

// feature-components
import PortfolioAnalysisFilterInput from '@/features/energyxplorer/portfolio/components/meter-drawer/charts/PortfolioAnalysisFilterInput';

// relative
import HourAnalysisChart from '../../../../components/analysis/HourAnalysisChart';

interface FilterState {
  activePeriodFilter: boolean | undefined;
  activeDaysFilter: boolean | undefined;
  activeHolidaysFilter: boolean | undefined;
}

interface Props {
  data?: object | null;
  isLoading?: boolean;
  setFilterOptions: (filters: FilterState) => void;
  unit?: string;
}

const HourAnalysisDialog = ({
  data = null,
  isLoading = true,
  setFilterOptions,
  unit,
}: Props) => {
  const { t } = useTranslation('energyxplorer-analysis-tabs');
  const handleApplyFilters = async (filters: FilterState) => {
    setFilterOptions(filters);
  };

  const [viewMode, setViewMode] = useState('column');

  return (
    <>
      <Box display={'flex'} flexDirection={'column'}>
        <Box display={'flex'} gap={2} sx={{ mt: 2 }}>
          <Grid container columns={12} width={'100%'}>
            <Grid
              size={{
                md: 7,
              }}
            >
              {data ? (
                <HourAnalysisChart
                  data={data}
                  viewMode={viewMode}
                  isLoading={isLoading}
                  unit={unit}
                />
              ) : (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <AlertTitle>{t('common.no_data_available')}</AlertTitle>
                  {t('hour_analysis.missing_children_description')}
                </Alert>
              )}
            </Grid>

            <Grid
              size={{
                md: 5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  fontWeight="bold"
                >
                  {t('common.show')}
                </Typography>
                <InfoTooltipIcon
                  title={t('energyxplorer:tooltips.applyFilteringForInsight')}
                />
              </Box>
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                sx={{ my: 2 }}
              >
                <Tooltip
                  title={t('energyxplorer:tooltips.showInColumns')}
                  placement="top"
                >
                  <IconButton
                    onClick={() => setViewMode('column')}
                    sx={{
                      borderRadius: 3,
                      mr: 1,
                    }}
                  >
                    <BarChartRoundedIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip
                  title={t('energyxplorer:tooltips.showAsBoxplot')}
                  placement="top"
                >
                  <IconButton
                    onClick={() => setViewMode('box')}
                    sx={{
                      borderRadius: 3,
                    }}
                  >
                    <TuneRoundedIcon sx={{ transform: 'rotate(90deg)' }} />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  fontWeight="bold"
                >
                  {t('hour_analysis.filtering')}
                </Typography>
                <InfoTooltipIcon
                  title={t('energyxplorer:tooltips.applyFilteringForInsight')}
                />
              </Box>
              <PortfolioAnalysisFilterInput
                handleApply={handleApplyFilters}
                showPeriodFilter={false}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default HourAnalysisDialog;
